import { Container, Graphics, Sprite, Text, Texture } from "pixi.js";

import type { ShotFired, ShotHit } from "../../../shared/ArenaState";
import { FX, SHOT } from "../../../shared/Config";
import { pickSplash, SPLASH_PREFIX, splashOf } from "../../../shared/Fx";
import { wrapCoord, wrapDelta } from "../../../shared/Sailing";
import { shotScale, ShotSettings } from "../../../shared/Settings";
import { Bursts, effectsUnder, rollFor } from "../fx/Bursts";

/** The ball, from the `projectiles` sheet. The only shot there is — see design/Evolution.md. */
const BALL_FRAME = "projectiles/CannonBall";

/** How long the splash of a hit lasts, in seconds. */
const SPLASH_SECONDS = 0.45;
/** Radius the ring starts and ends at, in pixels. It opens outward from the point of impact. */
const SPLASH_RADIUS_START = 10;
const SPLASH_RADIUS_END = 62;
const SPLASH_WIDTH = 3;
/** The palette's cream and action red — see design/UI.md. */
const SPLASH_COLOR = 0xf2e2c4;
const PLUNDER_COLOR = 0xc8452f;
/** How far the plunder label drifts up over its life, in pixels. */
const PLUNDER_RISE = 34;
const PLUNDER_SIZE = 18;

/**
 * How far off screen a ball is still drawn, in **world** pixels — everything here is measured in
 * those, because the viewport it is checked against is the screen divided by the camera's zoom.
 *
 * Whichever is wider: the ring a hit opens, or half of the biggest a ball is ever drawn. The ball
 * is the half that moves with a dial, and a lofted shot at a large `arcHeight` is easily wider
 * than the ring — without it a ball crossing the edge of the view would pop rather than slide.
 */
let CULL_MARGIN = cullMarginFor(SHOT);

/** The margin above, for whichever dials are in force. */
function cullMarginFor(dials: ShotSettings): number {
    return Math.max(SPLASH_RADIUS_END, (dials.size * (1 + dials.arcHeight)) / 2);
}

/** The shot dials in force. `SHOT` in the game; the dashboard's workspace swaps them — see below. */
let DIALS: ShotSettings = SHOT;

/**
 * Puts new numbers behind the ball.
 *
 * **For the dashboard's workspace and nothing else**, exactly as `setWaveDials` is — see the note
 * there. The arc is a picture drawn over a flight both ends already agree on to the pixel, so how
 * big a ball is drawn along the way cannot desync anything.
 */
export function setShotDials(values: ShotSettings): void {
    DIALS = values;
    CULL_MARGIN = cullMarginFor(values);
}

/**
 * How far outside the view a shot may fall and still throw up water, in pixels.
 *
 * Generous rather than exact. The camera moves while a splash plays, so a shot that drops just
 * off the edge can be on screen a tenth of a second later, and water that appeared already half
 * spent would read worse than water nobody saw. Past this there is no point: a ball can fall
 * anywhere in a sea twelve thousand pixels across, and most fall where nobody is looking.
 */
const SPLASH_SPAWN_MARGIN = 400;

/** One cannonball in the air, as this client is reckoning it. */
interface Flight {
    x: number;
    y: number;
    dirX: number;
    dirY: number;
    speed: number;
    range: number;
    travelled: number;
    sprite: Sprite;
}

/**
 * The mark a **hit** leaves: a ring opening on the water, and what it cost the ship that wore it.
 *
 * Not to be confused with the water a spent ball throws up, which is `_splashes` below and a
 * different event entirely — a ball that found a hull did not land in the sea.
 */
interface Impact {
    x: number;
    y: number;
    age: number;
    label: Text;
}

/**
 * Every cannonball in the sea, and the two different marks one leaves when it stops.
 *
 * A ball that **hits** gets a ring opening on the water and the plunder it moved — `land`, off
 * the server's `hit`. A ball that simply **runs out of range** drops into the sea and throws up
 * water: `_splash`, decided here and never told to anyone, because by then the flight is over on
 * both ends and no rule reads it. The two are different events and neither draws the other's
 * mark — a ball that found a hull did not land in the water.
 *
 * **The client flies its own shots.** A ball is not in `ArenaState`: the server broadcasts one
 * `shot` as it leaves the muzzle — from here, that way, this fast, this far — and every client
 * dead-reckons the flight from those five numbers with the same arithmetic the server runs. A
 * straight line at a constant speed cannot drift, so there is nothing to interpolate and
 * nothing to correct. See design/Networking.md.
 *
 * That leaves exactly one thing the server has to say afterwards, and it says it: a `hit`,
 * which ends a flight early. A shot nobody hears about simply runs out of range on both sides
 * at the same instant, because both sides are dividing the same range by the same speed.
 *
 * Like the loot, it sits **above** the water rather than inside it: a ball is a thing you read
 * a bearing off, and the displacement filter would warp it away from where it actually is. The
 * splash inherits that, which is a compromise rather than a decision — water in water is exactly
 * what the swell should be bending — but it is one container and one filter, and moving the
 * splash under the displacement means giving it its own. See design/Water.md.
 */
export class ShotField {
    private readonly _container = new Container();
    private readonly _balls = new Container();
    private readonly _rings = new Graphics();
    private readonly _labels = new Container();

    private readonly _flights = new Map<number, Flight>();
    private readonly _impacts: Impact[] = [];
    /**
     * The water thrown up where a spent ball drops into the sea. Decoration and nothing else: the
     * flight is over on both ends before one appears, and no rule anywhere reads it. Which art
     * and how big is authored in `config/fx.json` — see design/Settings.md.
     */
    private readonly _splashes = new Bursts();
    /** Retired sprites and labels, kept for the next shot rather than destroyed and remade. */
    private readonly _ballPool: Sprite[] = [];
    private readonly _labelPool: Text[] = [];

    /** What one world pixel of `SHOT.size` is as a sprite scale. Set by the first `_takeBall`. */
    private _ballScale = 0;

    private _width = 0;
    private _height = 0;

    public get container(): Container {
        return this._container;
    }

    constructor() {
        // Rings under the water, water under the balls, labels over everything: a ball still in
        // the air passes in front of a splash an earlier shot threw up, and a number is the
        // payoff and should never be hidden by the shot that earned it.
        this._container.addChild(this._rings, this._splashes.container, this._balls, this._labels);
    }

    /** Puts a broadcast shot in the air. Called straight off the `shot` message. */
    public add(shot: ShotFired): void {
        const sprite = this._takeBall();

        this._flights.set(shot.id, {
            x: shot.x,
            y: shot.y,
            // Heading 0 is north, so ahead is (sin, -cos) — the convention `advance` sails on.
            dirX: Math.sin(shot.heading),
            dirY: -Math.cos(shot.heading),
            speed: shot.speed,
            range: shot.range,
            travelled: 0,
            sprite
        });
    }

    /**
     * Ends a flight where the server says it ended, and marks the spot.
     *
     * The ring is drawn even for a shot this client never saw — a `hit` for an unknown id is a
     * client that joined mid-flight, and the splash is still true.
     */
    public land(hit: ShotHit): void {
        this._retire(hit.id);

        const label = this._takeLabel();
        label.text = `-${hit.plunder}`;
        this._impacts.push({ x: hit.x, y: hit.y, age: 0, label });
    }

    /**
     * Flies every ball one frame and draws the lot against the camera.
     *
     * `wrapDelta`, not subtraction: a ball just over the west edge belongs a few pixels west of
     * a camera on the east edge, not most of an arena away. See design/Networking.md.
     */
    public update(cameraX: number, cameraY: number, deltaSeconds: number): void {
        const centreX = this._width / 2;
        const centreY = this._height / 2;

        for (const [id, flight] of this._flights) {
            const step = Math.min(flight.speed * deltaSeconds, flight.range - flight.travelled);

            flight.x = wrapCoord(flight.x + flight.dirX * step);
            flight.y = wrapCoord(flight.y + flight.dirY * step);
            flight.travelled += step;

            // Out of range: it falls into the sea, on the same step the server drops it. This is
            // the one place water comes from — a ball that found a hull did not land in the sea,
            // and `land` already marks that with a ring and a number.
            if (flight.travelled >= flight.range) {
                this._splash(id, flight.x, flight.y, cameraX, cameraY);
                this._retire(id);
                continue;
            }

            const x = centreX + wrapDelta(cameraX, flight.x);
            const y = centreY + wrapDelta(cameraY, flight.y);
            const onScreen = x > -CULL_MARGIN && x < this._width + CULL_MARGIN && y > -CULL_MARGIN && y < this._height + CULL_MARGIN;

            flight.sprite.visible = onScreen;
            if (onScreen) {
                flight.sprite.position.set(x, y);
                // The lob. There is no height anywhere in this game — the ball is a point on the
                // water on both ends, and it hits whatever it passes over whatever size it is
                // drawn at right now. Swelling it away from the deck and shrinking it back to the
                // sea is the only cue a top-down view has that the shot went *over* anything, and
                // it is a picture and nothing else. Authored in `config/shot.json`.
                flight.sprite.scale.set(this._ballScale * shotScale(DIALS, flight.travelled / flight.range));
            }
        }

        this._drawImpacts(cameraX, cameraY, deltaSeconds);

        // Splashes hold a **world** position and are re-projected every frame, exactly like the
        // balls above: one lives long enough for the camera to move under it, so a screen
        // coordinate baked in at spawn would slide off the water it belongs to.
        this._splashes.update(deltaSeconds, (sprite, worldX, worldY) => {
            const x = centreX + wrapDelta(cameraX, worldX);
            const y = centreY + wrapDelta(cameraY, worldY);

            sprite.visible = x > -CULL_MARGIN && x < this._width + CULL_MARGIN && y > -CULL_MARGIN && y < this._height + CULL_MARGIN;
            if (sprite.visible) sprite.position.set(x, y);
        });
    }

    public resize(width: number, height: number): void {
        this._width = width;
        this._height = height;
    }

    public destroy(): void {
        this._container.destroy({ children: true });
        this._flights.clear();
        this._impacts.length = 0;
        this._ballPool.length = 0;
        this._labelPool.length = 0;
    }

    /**
     * Throws up water where a spent ball hit the sea.
     *
     * Which splash, and how big, is authored in `config/fx.json` and drawn from the `fxA` sheet's
     * `water_splash` animations — the same weighted draw the muzzle flashes use, seeded off the
     * shot's own id so every client watching the same shot sees the same water. See shared/Fx.ts.
     *
     * The **spin is random**, off that same seed. The art is a foam disc with distinct blobs in
     * it, so a run of splashes all at one angle reads as the same stamp pressed over and over.
     * There is no rotation dial for it, unlike a muzzle flash: a splash has no heading to be
     * wrong about, which is also why it is pinned by its centre rather than by a stored pivot.
     */
    private _splash(id: number, x: number, y: number, cameraX: number, cameraY: number): void {
        // Nowhere near the view: nothing worth drawing, and in a sea this size most shots end there.
        if (Math.abs(wrapDelta(cameraX, x)) > this._width / 2 + SPLASH_SPAWN_MARGIN) return;
        if (Math.abs(wrapDelta(cameraY, y)) > this._height / 2 + SPLASH_SPAWN_MARGIN) return;

        const roll = rollFor(id);
        const name = pickSplash(FX, effectsUnder(SPLASH_PREFIX), roll);
        if (!name) return;

        const splash = splashOf(FX, name);
        // The world point it happened at. `update`'s placement callback turns that into a screen
        // position every frame, so nothing here has to know where the camera is.
        this._splashes.spawn(name, x, y, roll * Math.PI * 2, splash.size, splash.fps, "centre");
    }

    /** Redraws every hit ring from scratch and drifts the numbers upward as they fade. */
    private _drawImpacts(cameraX: number, cameraY: number, deltaSeconds: number): void {
        this._rings.clear();

        const centreX = this._width / 2;
        const centreY = this._height / 2;

        for (let i = this._impacts.length - 1; i >= 0; i--) {
            const impact = this._impacts[i];
            impact.age += deltaSeconds;

            if (impact.age >= SPLASH_SECONDS) {
                this._releaseLabel(impact.label);
                this._impacts.splice(i, 1);
                continue;
            }

            const life = impact.age / SPLASH_SECONDS;
            const x = centreX + wrapDelta(cameraX, impact.x);
            const y = centreY + wrapDelta(cameraY, impact.y);

            impact.label.visible = true;
            impact.label.position.set(x, y - PLUNDER_RISE * life);
            impact.label.alpha = 1 - life * life;

            this._rings
                .circle(x, y, SPLASH_RADIUS_START + (SPLASH_RADIUS_END - SPLASH_RADIUS_START) * life)
                .stroke({ color: SPLASH_COLOR, width: SPLASH_WIDTH, alpha: 1 - life });
        }
    }

    /** Takes a flight off the books and parks its sprite. Unknown ids are simply ignored. */
    private _retire(id: number): void {
        const flight = this._flights.get(id);
        if (!flight) return;

        flight.sprite.visible = false;
        this._ballPool.push(flight.sprite);
        this._flights.delete(id);
    }

    /**
     * A ball to fly, pooled. Its scale is reset to the muzzle size on the way out — a pooled
     * sprite retired at whatever the arc had it at, and the first `update` is a frame away.
     *
     * `_ballScale` is resolved here, off the texture, rather than in the constructor: the sheet
     * is not loaded until the preloader has run. A pooled ball can only exist because one was
     * made this way first, so the pooled branch always finds it set.
     */
    private _takeBall(): Sprite {
        const pooled = this._ballPool.pop();
        if (pooled) {
            pooled.visible = true;
            // Read off the dials rather than off the cache, so a ball fired after the dashboard
            // has moved `size` is the size it now asks for. In the game the two never differ.
            this._ballScale = DIALS.size / pooled.texture.height;
            pooled.scale.set(this._ballScale);
            return pooled;
        }

        const sprite = new Sprite({ texture: Texture.from(BALL_FRAME), anchor: 0.5 });
        // Scaled off the untrimmed frame, the same rule a hull is sized by — see design/Ships.md.
        // Kept rather than baked into the sprite: `update` multiplies it by the arc every frame.
        this._ballScale = DIALS.size / sprite.texture.height;
        sprite.scale.set(this._ballScale);
        this._balls.addChild(sprite);

        return sprite;
    }

    private _takeLabel(): Text {
        const pooled = this._labelPool.pop();
        if (pooled) return pooled;

        const label = new Text({
            text: "",
            anchor: 0.5,
            visible: false,
            style: {
                fontFamily: "Grobold",
                fontSize: PLUNDER_SIZE,
                fill: PLUNDER_COLOR,
                stroke: { color: 0x0b2a35, width: 4, join: "round" }
            }
        });
        this._labels.addChild(label);

        return label;
    }

    private _releaseLabel(label: Text): void {
        label.visible = false;
        this._labelPool.push(label);
    }
}
